import {
  alpha,
  Box,
  styled,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Skeleton,
} from "@mui/material";
import _ from "lodash";

export interface ColumnProps {
  dataIndex: string;
  key: string;
  title: any;
  render?: (cellData: any, row: any) => any;
  textColor?: string;
}

interface TableProps {
  columns: Array<ColumnProps>;
  rowKey?: string;
  dataSource?: any[];
  loading?: boolean;
  headAlign?: any;
  tableAlign?: any;
  onRowClick?: (row: any) => void;
  pagination?: {
    page: number;
    limit: number;
    totalCount: number;
    onChange: (v: number) => void;
    onChangeLimit?: (v: number) => void;
  };
}

// Modern styled components
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: "12px",
  border: "1px solid #E2E8F0",
  overflow: "hidden",
  backgroundColor: "#FFFFFF",
}));

const StyledTableHead = styled(TableHead)({
  backgroundColor: "#F8FAFC",
});

const StyledHeadCell = styled(TableCell)({
  padding: "14px 16px",
  borderBottom: "1px solid #E2E8F0",
  fontWeight: 600,
  fontSize: "13px",
  color: "#64748B",
  textTransform: "none",
  whiteSpace: "nowrap",
});

const StyledBodyCell = styled(TableCell)({
  padding: "14px 16px",
  borderBottom: "1px solid #F1F5F9",
  fontSize: "14px",
  color: "#1E293B",
});

const StyledTableRow = styled(TableRow)<{ canRowClick: boolean }>(
  ({ canRowClick }) => ({
    transition: "background-color 0.15s ease",
    "&:last-child td": {
      borderBottom: "none",
    },
    ...(canRowClick && {
      cursor: "pointer",
      "&:hover": {
        backgroundColor: "#F8FAFC",
      },
    }),
  })
);

const EmptyStateBox = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "48px 24px",
  color: "#64748B",
});

export default function Table({
  columns,
  dataSource,
  onRowClick,
  pagination,
  loading,
  headAlign = "left",
  tableAlign = "left",
}: TableProps) {
  // Loading skeleton
  if (loading) {
    return (
      <StyledTableContainer>
        <MuiTable>
          <StyledTableHead>
            <TableRow>
              {columns.map((col, index) => (
                <StyledHeadCell align={headAlign} key={index}>
                  {col.title}
                </StyledHeadCell>
              ))}
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {[...Array(5)].map((_, rowIndex) => (
              <TableRow key={rowIndex}>
                {columns.map((_, colIndex) => (
                  <StyledBodyCell key={colIndex}>
                    <Skeleton variant="text" width="80%" height={20} />
                  </StyledBodyCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </MuiTable>
      </StyledTableContainer>
    );
  }

  return (
    <StyledTableContainer>
      <MuiTable>
        <StyledTableHead>
          <TableRow>
            {columns.map((col, index) => (
              <StyledHeadCell align={headAlign} key={index}>
                {col.title}
              </StyledHeadCell>
            ))}
          </TableRow>
        </StyledTableHead>
        {dataSource && dataSource.length > 0 ? (
          <TableBody>
            {dataSource.map((row, index) => (
              <StyledTableRow
                canRowClick={!!onRowClick}
                key={index}
                onClick={() => onRowClick && onRowClick(row)}
              >
                {columns.map((col, colIndex) => (
                  <StyledBodyCell
                    align={tableAlign}
                    sx={{ color: col.textColor }}
                    key={colIndex}
                  >
                    {col?.render ? (
                      col.render(_.get(row, col.dataIndex), row)
                    ) : (
                      _.get(row, col.dataIndex) || "—"
                    )}
                  </StyledBodyCell>
                ))}
              </StyledTableRow>
            ))}
          </TableBody>
        ) : (
          <TableBody>
            <TableRow>
              <TableCell colSpan={columns.length} sx={{ border: "none" }}>
                <EmptyStateBox>
                  <Typography variant="body1" fontWeight={500} color="#64748B">
                    No records found
                  </Typography>
                  <Typography variant="body2" color="#94A3B8" sx={{ mt: 0.5 }}>
                    Try adjusting your search or filters
                  </Typography>
                </EmptyStateBox>
              </TableCell>
            </TableRow>
          </TableBody>
        )}
      </MuiTable>
    </StyledTableContainer>
  );
}
